import {
  defaultReportRunClient,
  describeFailedRun,
  describeRunError,
  ReportRunClient,
} from '@/utils/customActionsReports';
import type { ReportRunInput } from '@/utils/reportRunsV3';

/**
 * Inventory View dashboard → Download Summary, as a V3 report run.
 *
 * The dashboard table pages 100 rows at a time ("Load More"); the legacy
 * Download Summary only exported the pages already loaded. With the org flag
 * on, the button instead runs api-svc's `inventory-dashboard-summary-report`,
 * which walks every page server-side and returns the complete CSV. The table
 * itself is untouched.
 */

/** Org flag gating the full-summary download (also enforced server-side). */
export const INVENTORY_DASHBOARD_SUMMARY_FLAG = 'inventory-dashboard-summary-v3';

/** The api-svc report run type behind the full-summary download. */
export const INVENTORY_DASHBOARD_SUMMARY_REPORT_TYPE = 'inventory-dashboard-summary-report';

export const SUMMARY_FALLBACK_FILENAME = 'dashboard-summary.csv';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

/** The dashboard's current selections: what the table on screen was loaded with. */
export interface DashboardSummaryRequest {
  inventoryViewId: string;
  /** The As Of the table was last submitted with (not the unsubmitted date picker value). */
  asOf: string;
  groupByInventory: boolean;
  includeSubsidiary: boolean;
  ignoreInternalTransfers: boolean;
}

export type DashboardSummaryDownloadResult =
  | { status: 'downloaded' }
  | { status: 'aborted' }
  | { status: 'error'; message: string };

export function summaryRunInputs(req: DashboardSummaryRequest): ReportRunInput[] {
  return [
    { key: 'inventoryViewId', value: req.inventoryViewId },
    { key: 'asOf', value: req.asOf },
    { key: 'groupByInventory', value: String(req.groupByInventory) },
    { key: 'includeSubsidiary', value: String(req.includeSubsidiary) },
    { key: 'ignoreInternalTransfers', value: String(req.ignoreInternalTransfers) },
  ];
}

/**
 * Submits the run, waits for it, and saves the CSV. Never throws: every
 * failure comes back as `{ status: 'error', message }` for the snackbar.
 * `orgId` is passed in (and used for all three calls) so an org switch
 * mid-run cannot send the later calls to a different org than the run's.
 */
export async function downloadFullDashboardSummary(
  baseUrl: string,
  orgId: string,
  req: DashboardSummaryRequest,
  isAborted: () => boolean = () => false,
  client: ReportRunClient = defaultReportRunClient
): Promise<DashboardSummaryDownloadResult> {
  try {
    const runId = await client.submit(baseUrl, orgId, INVENTORY_DASHBOARD_SUMMARY_REPORT_TYPE, summaryRunInputs(req));
    const result = await client.poll(baseUrl, orgId, runId, {
      intervalMs: POLL_INTERVAL_MS,
      timeoutMs: POLL_TIMEOUT_MS,
      isAborted,
    });
    if (result.outcome === 'aborted') {
      return { status: 'aborted' };
    }
    if (result.outcome === 'timed-out') {
      return { status: 'error', message: 'The summary timed out. Please try again.' };
    }
    if (result.outcome === 'failed') {
      return { status: 'error', message: describeFailedRun(result.errors) };
    }
    await client.download(baseUrl, orgId, runId, SUMMARY_FALLBACK_FILENAME);
    return { status: 'downloaded' };
  } catch (err) {
    return { status: 'error', message: describeRunError(err) };
  }
}
